<template>
  <div class="card border-2 border-psychology-primary/20">
    <div class="p-6 text-center pb-6">
      <!-- 图标 -->
      <div class="w-20 h-20 bg-psychology-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10 text-psychology-primary">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
        </svg>
      </div>

      <!-- 标题 -->
      <div class="text-2xl sm:text-3xl font-bold text-psychology-primary mb-2">
        恋爱占有欲指数 (RPI)
      </div>

      <!-- 分数 -->
      <div class="text-4xl sm:text-6xl font-bold text-psychology-primary mb-4">
        {{ rpiScore }}
      </div>

      <!-- 等级标签 -->
      <div 
        :class="[
          'inline-flex items-center rounded-full border font-semibold text-lg px-6 py-2',
          getLevelColorClass(rpiLevel.color)
        ]"
      >
        {{ rpiLevel.level }}
      </div>
    </div>

    <div class="p-6 pt-0 space-y-6">
      <!-- 进度条 -->
      <div class="text-center">
        <p class="text-gray-600 mb-4">
          {{ pronoun }}恋爱占有欲指数为 <span class="font-semibold text-psychology-primary">{{ rpiScore }}</span>，
          处于 <span class="font-semibold">{{ rpiLevel.level }}</span> 水平
        </p>
        <div class="max-w-2xl mx-auto">
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-3 mb-2">
            <div 
              class="h-full bg-psychology-primary transition-all duration-500"
              :style="{ width: rpiScore + '%' }"
            ></div>
          </div>
          <div class="flex justify-between text-xs text-gray-500">
            <span>0 (自由自在)</span>
            <span>50 (恰到好处)</span>
            <span>100 (爱到极致)</span>
          </div>
        </div>
      </div>

      <div class="h-px bg-gray-200"></div>

      <!-- 结果解释 -->
      <div>
        <h3 class="font-semibold text-lg mb-3 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-psychology-primary">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 16v-4"></path>
            <path d="M12 8h.01"></path>
          </svg>
          结果解释
        </h3>
        <div class="space-y-2">
          <p 
            v-for="(text, index) in interpretation" 
            :key="index"
            class="text-gray-600 leading-relaxed"
          >
            {{ text }}
          </p>
        </div>
      </div>

      <!-- 个性化建议 -->
      <div>
        <h3 class="font-semibold text-lg mb-3 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-psychology-primary">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
            <polyline points="16 7 22 7 22 13"></polyline>
          </svg>
          个性化建议
        </h3>
        <div class="space-y-2">
          <div 
            v-for="(recommendation, index) in recommendations"
            :key="index"
            class="flex items-start gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="`w-4 h-4 mt-0.5 flex-shrink-0 ${getLevelIconColor(rpiLevel.color)}`">
              <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
              <path d="m9 11 3 3L22 4"></path>
            </svg>
            <p class="text-gray-600 leading-relaxed">{{ recommendation }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RPIScoreCard',
  props: {
    rpiScore: {
      type: Number,
      required: true
    },
    rpiLevel: {
      type: Object,
      required: true
    },
    recommendations: {
      type: Array,
      default: () => []
    },
    interpretation: {
      type: Array,
      default: () => []
    },
    assessmentType: {
      type: String,
      default: 'self'
    }
  },
  computed: {
    pronoun() {
      return this.assessmentType === 'self' ? '你的' : 'Ta的'
    }
  },
  methods: {
    getLevelColorClass(color) {
      const colorMap = {
        blue: 'text-blue-600 bg-blue-50 border-blue-200',
        green: 'text-green-600 bg-green-50 border-green-200',
        yellow: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        orange: 'text-orange-600 bg-orange-50 border-orange-200',
        red: 'text-red-600 bg-red-50 border-red-200'
      }
      return colorMap[color] || colorMap.blue
    },
    getLevelIconColor(color) {
      const colorMap = {
        blue: 'text-blue-500',
        green: 'text-green-500',
        yellow: 'text-yellow-500',
        orange: 'text-orange-500',
        red: 'text-red-500'
      }
      return colorMap[color] || colorMap.blue
    },
    getLevelDescription(score) {
      if (score < 30) return '很低'
      if (score < 50) return '较低'
      if (score < 70) return '中等'
      if (score < 85) return '较高'
      return '很高'
    }
  }
}
</script>

